import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';

import { Callout, CopyButton, ErrorCallout, InlineConfirm, PageHead, Spinner } from '../components/ui';
import { api } from '../lib/api';
import { fullTime, shortDate, span } from '../lib/format';
import { useAction, useDocumentTitle, useResource } from '../lib/hooks';
import { useSession } from '../lib/session';
import type { TeamDetail } from '../lib/types';

const REGION_NAMES: Record<string, string> = { us: 'United States' };

export function SettingsPage() {
  useDocumentTitle('Settings');
  const { me, refreshMe, signOut } = useSession();
  const navigate = useNavigate();
  const team = useResource<TeamDetail>('/v1/team');
  const [name, setName] = useState('');
  const [saved, setSaved] = useState(false);
  const rename = useAction(async (next: string) => api<TeamDetail>('/v1/team', { method: 'PATCH', body: { name: next } }));
  const leave = useAction(async (userId: string) => {
    await api(`/v1/team/members/${encodeURIComponent(userId)}`, { method: 'DELETE' });
    return true;
  });

  useEffect(() => {
    if (team.data) setName(team.data.name);
  }, [team.data]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    const updated = await rename.run(name.trim());
    if (updated) {
      team.setData(updated);
      setSaved(true);
      await refreshMe();
    }
  }

  /* Leaving moves the session to another of your accounts. */
  async function leaveOrganization() {
    if (!me) return;
    if (!(await leave.run(me.user.id))) return;
    if (await refreshMe()) navigate('/', { replace: true });
  }

  const t = team.data;
  const isOwner = me?.role === 'owner';
  const personal = (t?.kind ?? me?.team.kind) === 'personal';
  const noun = personal ? 'account' : 'organization';
  const dirty = t !== undefined && name.trim() !== t.name && name.trim().length > 0;

  return (
    <>
      <PageHead title="Settings" lede={personal ? 'Your personal account.' : `Settings for ${me?.team.name ?? 'this organization'}.`} />
      <section className="band">
        <div className="band__head">
          <h2 className="h2">{personal ? 'Account' : 'Organization'}</h2>
        </div>
        {team.error ? <ErrorCallout error={team.error} onRetry={() => void team.reload()} /> : null}
        <div className="stack" style={{ maxWidth: 640 }}>
          <form className="inline-form" onSubmit={submit}>
            <div className="field" style={{ maxWidth: 420 }}>
              <label htmlFor="team-name">Name</label>
              <input
                id="team-name"
                className="input"
                value={name}
                maxLength={80}
                onChange={(e) => {
                  setName(e.target.value);
                  setSaved(false);
                }}
                disabled={!t || !isOwner}
                aria-describedby={isOwner ? undefined : 'team-name-note'}
              />
            </div>
            {isOwner ? (
              <button type="submit" className="btn btn--primary" disabled={!dirty || rename.busy} aria-busy={rename.busy} style={{ minHeight: 38 }}>
                {rename.busy ? <Spinner /> : null}
                Save
              </button>
            ) : null}
          </form>
          {!isOwner ? (
            <p className="hint" id="team-name-note" style={{ margin: '-6px 0 0' }}>
              Only an owner can rename the {noun}.
            </p>
          ) : null}
          {rename.error ? <ErrorCallout error={rename.error} /> : null}
          {saved ? <Callout tone="ok" role="status" title="Saved." /> : null}
          {t ? (
            <dl className="spec mt-8">
              <div>
                <dt>ID</dt>
                <dd className="row" style={{ gap: 4 }}>
                  <code>{t.id}</code>
                  <CopyButton text={t.id} label={`Copy ${noun} id`} />
                </dd>
              </div>
              <div>
                <dt>Type</dt>
                <dd>{personal ? 'Personal' : 'Organization'}</dd>
              </div>
              <div>
                <dt>Region</dt>
                <dd>{REGION_NAMES[t.region] ?? t.region}</dd>
              </div>
              <div>
                <dt>Created</dt>
                <dd>{shortDate(t.createdAt)}</dd>
              </div>
            </dl>
          ) : null}
        </div>
      </section>
      <section className="band">
        <div className="band__head">
          <div>
            <h2 className="h2">Limits</h2>
            <p>Need more? Ask us.</p>
          </div>
        </div>
        {t ? (
          <dl className="spec" style={{ maxWidth: 640 }}>
            <div>
              <dt>Open sandboxes</dt>
              <dd>{t.limits.concurrentSandboxes} at once</dd>
            </div>
            <div>
              <dt>Sandbox timeout</dt>
              <dd>up to {span(t.limits.maxSandboxTimeoutMs)}</dd>
            </div>
            <div>
              <dt>Task timeout</dt>
              <dd>up to {span(t.limits.maxTaskTimeoutMs)}</dd>
            </div>
          </dl>
        ) : (
          <span className="skeleton" style={{ width: 280 }} />
        )}
      </section>
      <section className="band">
        <div className="band__head">
          <h2 className="h2">You</h2>
        </div>
        {me ? (
          <dl className="spec" style={{ maxWidth: 640 }}>
            <div>
              <dt>Email</dt>
              <dd>{me.user.email}</dd>
            </div>
            <div>
              <dt>Name</dt>
              <dd>{me.user.name || <span className="muted">Not set</span>}</dd>
            </div>
            {personal ? null : (
              <div>
                <dt>Role</dt>
                <dd>{me.role === 'owner' ? 'Owner' : 'Member'}</dd>
              </div>
            )}
            <div>
              <dt>Joined</dt>
              <dd title={fullTime(me.user.createdAt)}>{shortDate(me.user.createdAt)}</dd>
            </div>
          </dl>
        ) : null}
        <div className="row mt-16" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn" onClick={() => void signOut()}>
            <LogOut aria-hidden="true" />
            Sign out
          </button>
          {personal || !me ? null : (
            <InlineConfirm
              trigger="Leave organization"
              question={`Leave ${me.team.name}? Your keys on it stop working.`}
              confirmLabel="Leave"
              size=""
              triggerClass="btn"
              busy={leave.busy}
              onConfirm={leaveOrganization}
            />
          )}
        </div>
        {leave.error ? (
          <div className="mt-16">
            <ErrorCallout error={leave.error} title={leave.error.code === 'last-owner' ? 'Make someone else an owner first' : undefined} />
          </div>
        ) : null}
      </section>
    </>
  );
}
